import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../img/logo_w.png';
import {
  Menu,
  MenuItem,
  Typography,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Dashboard,
  ChatBubbleOutline,
  PeopleOutline,
  ReplyOutlined,
  MessageOutlined,
  PhotoLibraryOutlined,
  NotificationsOutlined,
  Business,
  InventoryOutlined,
  WhatsApp,
  TrendingUp,
  PersonOutline,
  Logout,
  Settings,
  MenuOpen,
  Menu as MenuIcon,
  SupervisorAccount,
  SmartToy,
  History as HistoryIcon,
  ShoppingCartOutlined,
  ExpandMore,
  ExpandLess,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { useWhatsApp } from '../contexts/WhatsAppContext';
import { configHasSalesBot } from '../constants/botTypes';
import NotificationBell from './NotificationBell';
import '../App.css';

const SIDEBAR_WIDTH = 240;

interface NavItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  badge?: number;
}

interface NavGroup {
  text: string;
  icon: React.ReactNode;
  id: string;
  children: NavItem[];
}

type NavEntry = NavItem | NavGroup;

interface NavSection {
  label?: string;
  items: NavEntry[];
}

interface LayoutProps {
  children: React.ReactNode;
}

const isNavGroup = (entry: NavEntry): entry is NavGroup => 'children' in entry;

const flattenNavItems = (sections: NavSection[]): NavItem[] =>
  sections.flatMap((section) =>
    section.items.flatMap((item) => (isNavGroup(item) ? item.children : [item])),
  );

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [groupMenuAnchor, setGroupMenuAnchor] = useState<null | HTMLElement>(null);
  const [groupMenuItems, setGroupMenuItems] = useState<NavItem[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const navigate = useNavigate();
  const location = useLocation();
  const { state: authState, logout } = useAuth();
  const { isConnected } = useSocket();
  const { whatsappNumbers } = useWhatsApp();

  const user = authState.user;
  const hasPersonalNumber = whatsappNumbers.some((n) => n.provider === 'personal_web');
  const hasSalesBot = whatsappNumbers.some(configHasSalesBot);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) setCollapsed(true);
      else setCollapsed(false);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    setAnchorEl(null);
    logout();
    navigate('/login');
  };

  const isActive = (path: string) =>
    location.pathname === path || (path !== '/dashboard' && location.pathname.startsWith(path));

  const isGroupActive = (group: NavGroup) => group.children.some((child) => isActive(child.path));

  const getSections = (): NavSection[] => {
    const role = user?.role;

    const whatsAppTemplatesGroup: NavGroup = {
      id: 'whatsapp-templates',
      text: 'WhatsApp Templates',
      icon: <MessageOutlined fontSize="small" />,
      children: [
        { text: 'Templates', icon: <MessageOutlined fontSize="small" />, path: '/whatsapp-templates' },
        { text: 'Sent History', icon: <HistoryIcon fontSize="small" />, path: '/template-send-history' },
      ],
    };

    const personalWhatsAppChildren: NavItem[] = role === 'agent'
      ? [
          { text: 'Connect', icon: <LinkIcon fontSize="small" />, path: '/agent/personal-whatsapp' },
          ...(hasPersonalNumber
            ? [
                { text: 'Personal Templates', icon: <MessageOutlined fontSize="small" />, path: '/personal-templates' },
                { text: 'Personal Sent History', icon: <HistoryIcon fontSize="small" />, path: '/personal-template-send-history' },
              ]
            : []),
        ]
      : [
          { text: 'Personal Templates', icon: <MessageOutlined fontSize="small" />, path: '/personal-templates' },
          { text: 'Personal Sent History', icon: <HistoryIcon fontSize="small" />, path: '/personal-template-send-history' },
        ];

    const personalWhatsAppGroup: NavGroup = {
      id: 'personal-whatsapp',
      text: 'Personal WhatsApp',
      icon: <WhatsApp fontSize="small" />,
      children: personalWhatsAppChildren,
    };

    if (role === 'super_admin') {
      return [
        {
          label: 'Overview',
          items: [
            { text: 'Dashboard', icon: <Dashboard fontSize="small" />, path: '/dashboard' },
            { text: 'Analytics', icon: <TrendingUp fontSize="small" />, path: '/super-admin/analytics' },
          ],
        },
        {
          label: 'Management',
          items: [
            { text: 'Companies', icon: <Business fontSize="small" />, path: '/super-admin/companies' },
            { text: 'Users', icon: <SupervisorAccount fontSize="small" />, path: '/super-admin/users' },
            { text: 'Packages', icon: <InventoryOutlined fontSize="small" />, path: '/super-admin/packages' },
            { text: 'WhatsApp Configs', icon: <WhatsApp fontSize="small" />, path: '/super-admin/whatsapp-configs' },
            { text: 'Bot Tester', icon: <SmartToy fontSize="small" />, path: '/super-admin/booking-bot-test' },
            { text: 'Sales Inquiries', icon: <ShoppingCartOutlined fontSize="small" />, path: '/super-admin/sales-inquiries' },
          ],
        },
        {
          label: 'System',
          items: [
            { text: 'Notifications', icon: <NotificationsOutlined fontSize="small" />, path: '/notifications' },
          ],
        },
      ];
    }

    if (role === 'admin') {
      const engageItems: NavEntry[] = [
        { text: 'Contacts', icon: <PeopleOutline fontSize="small" />, path: '/contacts' },
        { text: 'Quick Replies', icon: <ReplyOutlined fontSize="small" />, path: '/quick-replies' },
        whatsAppTemplatesGroup,
        ...(hasPersonalNumber ? [personalWhatsAppGroup] : []),
        { text: 'Media Gallery', icon: <PhotoLibraryOutlined fontSize="small" />, path: '/media-gallery' },
      ];

      return [
        {
          label: 'Overview',
          items: [
            { text: 'Dashboard', icon: <Dashboard fontSize="small" />, path: '/dashboard' },
            { text: 'Chat', icon: <ChatBubbleOutline fontSize="small" />, path: '/chat' },
          ],
        },
        {
          label: 'Engage',
          items: engageItems,
        },
        {
          label: 'Admin',
          items: [
            { text: 'Agents', icon: <PersonOutline fontSize="small" />, path: '/admin/agents' },
            ...(hasSalesBot
              ? [{ text: 'Sales Inquiries', icon: <ShoppingCartOutlined fontSize="small" />, path: '/admin/sales-inquiries' }]
              : []),
            { text: 'WA Settings', icon: <WhatsApp fontSize="small" />, path: '/admin/whatsapp-configs' },
            { text: 'Notifications', icon: <NotificationsOutlined fontSize="small" />, path: '/notifications' },
          ],
        },
      ];
    }

    return [
      {
        label: 'Overview',
        items: [
          { text: 'Dashboard', icon: <Dashboard fontSize="small" />, path: '/dashboard' },
          { text: 'Chat', icon: <ChatBubbleOutline fontSize="small" />, path: '/chat' },
        ],
      },
      {
        label: 'Tools',
        items: [
          { text: 'Contacts', icon: <PeopleOutline fontSize="small" />, path: '/contacts' },
          personalWhatsAppGroup,
          { text: 'Notifications', icon: <NotificationsOutlined fontSize="small" />, path: '/notifications' },
        ],
      },
    ];
  };

  const sections = useMemo(() => getSections(), [user?.role, hasPersonalNumber, hasSalesBot]);

  useEffect(() => {
    const nextExpanded: Record<string, boolean> = {};
    sections.forEach((section) => {
      section.items.forEach((item) => {
        if (isNavGroup(item) && item.children.some((child) => isActive(child.path))) {
          nextExpanded[item.id] = true;
        }
      });
    });
    if (Object.keys(nextExpanded).length > 0) {
      setExpandedGroups((prev) => ({ ...prev, ...nextExpanded }));
    }
  }, [location.pathname, sections]);

  const sidebarWidth = collapsed ? 70 : SIDEBAR_WIDTH;

  const allItems = flattenNavItems(sections);
  const currentItem = allItems.find((item) => isActive(item.path));
  const pageTitle = currentItem?.text || 'ChatPro';

  const roleLabel = (role?: string) => {
    if (role === 'super_admin') return 'Super Admin';
    if (role === 'admin') return 'Admin';
    return 'Agent';
  };

  const userInitial = user?.name?.charAt(0)?.toUpperCase() || 'U';

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const openCollapsedGroupMenu = (event: React.MouseEvent<HTMLElement>, group: NavGroup) => {
    setGroupMenuAnchor(event.currentTarget);
    setGroupMenuItems(group.children);
  };

  const handleNavClick = (path: string) => {
    setGroupMenuAnchor(null);
    navigate(path);
  };

  const renderNavButton = (item: NavItem, options?: { nested?: boolean }) => {
    const active = isActive(item.path);
    const nested = options?.nested ?? false;

    return (
      <button
        key={item.path}
        onClick={() => handleNavClick(item.path)}
        title={collapsed && !nested ? item.text : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          width: '100%',
          padding: collapsed && !nested ? '10px' : nested ? '8px 10px 8px 28px' : '9px 10px',
          borderRadius: 10,
          border: `1px solid ${active ? 'rgba(34,197,94,0.18)' : 'transparent'}`,
          background: active ? 'rgba(34,197,94,0.1)' : 'transparent',
          color: active ? '#22c55e' : nested ? '#94a3b8' : '#94a3b8',
          cursor: 'pointer',
          transition: 'all 150ms ease',
          marginBottom: 2,
          position: 'relative',
          justifyContent: collapsed && !nested ? 'center' : 'flex-start',
          textAlign: 'left',
          fontFamily: 'Inter, sans-serif',
        }}
        onMouseEnter={(e) => {
          if (!active) {
            (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.06)';
            (e.currentTarget as HTMLButtonElement).style.color = '#e2e8f0';
          }
        }}
        onMouseLeave={(e) => {
          if (!active) {
            (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            (e.currentTarget as HTMLButtonElement).style.color = nested ? '#94a3b8' : '#94a3b8';
          }
        }}
      >
        {active && (
          <span
            style={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 3,
              height: '60%',
              borderRadius: '0 3px 3px 0',
              background: '#22c55e',
              boxShadow: '0 0 8px rgba(34,197,94,0.5)',
            }}
          />
        )}
        <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{item.icon}</span>
        {(!collapsed || nested) && (
          <span
            style={{
              fontSize: nested ? '0.8125rem' : '0.875rem',
              fontWeight: active ? 600 : 500,
              flex: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              animation: 'fadeIn 0.2s ease-out',
            }}
          >
            {item.text}
          </span>
        )}
        {!collapsed && item.badge && item.badge > 0 && (
          <span
            style={{
              minWidth: 18,
              height: 18,
              borderRadius: 9,
              background: '#ef4444',
              color: 'white',
              fontSize: '0.65rem',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
              flexShrink: 0,
            }}
          >
            {item.badge}
          </span>
        )}
      </button>
    );
  };

  const renderNavGroup = (group: NavGroup) => {
    const groupActive = isGroupActive(group);
    const isExpanded = expandedGroups[group.id] ?? false;

    const groupHeader = (
      <button
        onClick={(e) => {
          if (collapsed) {
            openCollapsedGroupMenu(e, group);
          } else {
            toggleGroup(group.id);
          }
        }}
        title={collapsed ? group.text : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          width: '100%',
          padding: collapsed ? '10px' : '9px 10px',
          borderRadius: 10,
          border: `1px solid ${groupActive ? 'rgba(34,197,94,0.18)' : 'transparent'}`,
          background: groupActive ? 'rgba(34,197,94,0.1)' : 'transparent',
          color: groupActive ? '#22c55e' : '#94a3b8',
          cursor: 'pointer',
          transition: 'all 150ms ease',
          marginBottom: 2,
          position: 'relative',
          justifyContent: collapsed ? 'center' : 'flex-start',
          textAlign: 'left',
          fontFamily: 'Inter, sans-serif',
        }}
        onMouseEnter={(e) => {
          if (!groupActive) {
            (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.06)';
            (e.currentTarget as HTMLButtonElement).style.color = '#e2e8f0';
          }
        }}
        onMouseLeave={(e) => {
          if (!groupActive) {
            (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            (e.currentTarget as HTMLButtonElement).style.color = '#94a3b8';
          }
        }}
      >
        {groupActive && (
          <span
            style={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 3,
              height: '60%',
              borderRadius: '0 3px 3px 0',
              background: '#22c55e',
              boxShadow: '0 0 8px rgba(34,197,94,0.5)',
            }}
          />
        )}
        <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{group.icon}</span>
        {!collapsed && (
          <>
            <span
              style={{
                fontSize: '0.875rem',
                fontWeight: groupActive ? 600 : 500,
                flex: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                animation: 'fadeIn 0.2s ease-out',
              }}
            >
              {group.text}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0, color: 'inherit' }}>
              {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </span>
          </>
        )}
      </button>
    );

    return (
      <div key={group.id}>
        {collapsed ? (
          <Tooltip title={group.text} placement="right" arrow>
            {groupHeader}
          </Tooltip>
        ) : (
          groupHeader
        )}
        {!collapsed && isExpanded && (
          <div style={{ marginBottom: 2 }}>
            {group.children.map((child) => renderNavButton(child, { nested: true }))}
          </div>
        )}
      </div>
    );
  };

  const renderNavEntry = (entry: NavEntry) => {
    if (isNavGroup(entry)) {
      return renderNavGroup(entry);
    }

    const navBtn = renderNavButton(entry);
    return collapsed ? (
      <Tooltip key={entry.path} title={entry.text} placement="right" arrow>
        {navBtn}
      </Tooltip>
    ) : (
      navBtn
    );
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8fafc' }}>
      <aside
        style={{
          width: sidebarWidth,
          background: '#0f172a',
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1200,
          transition: 'width 280ms cubic-bezier(0.4,0,0.2,1)',
          overflowX: 'hidden',
          borderRight: '1px solid rgba(255,255,255,0.05)',
        }}
      >
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            padding: collapsed ? '0 18px' : '0 20px',
            borderBottom: '1px solid rgba(255,255,255,0.07)',
            flexShrink: 0,
            gap: 10,
            justifyContent: collapsed ? 'center' : 'flex-start',
          }}
        >
          {!collapsed && (
            <div style={{ overflow: 'hidden', animation: 'fadeIn 0.2s ease-out' }}>
              <img src={logo} style={{ height: 60 }} alt="ChatPro" className="login-brand-logo" />
            </div>
          )}
        </div>

        <nav style={{ flex: 1, padding: '12px 10px', overflowY: 'auto', overflowX: 'hidden' }}>
          {sections.map((section, si) => (
            <div key={si} style={{ marginBottom: 4 }}>
              {!collapsed && section.label && (
                <div
                  style={{
                    fontSize: '0.625rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: 'rgba(148,163,184,0.45)',
                    padding: '10px 10px 4px',
                    whiteSpace: 'nowrap',
                    animation: 'fadeIn 0.2s ease-out',
                  }}
                >
                  {section.label}
                </div>
              )}
              {section.items.map((item) => renderNavEntry(item))}
              {si < sections.length - 1 && !collapsed && (
                <div style={{ height: 1, background: 'rgba(255,255,255,0.05)', margin: '6px 0' }} />
              )}
            </div>
          ))}
        </nav>

        <div
          style={{
            padding: '12px 10px',
            borderTop: '1px solid rgba(255,255,255,0.07)',
            flexShrink: 0,
          }}
        >
          {!collapsed && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px 8px',
                animation: 'fadeIn 0.2s ease-out',
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: isConnected ? '#22c55e' : '#ef4444',
                  boxShadow: isConnected ? '0 0 6px #22c55e' : 'none',
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: '0.7rem', color: '#64748b', whiteSpace: 'nowrap' }}>
                {isConnected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
          )}
          <button
            onClick={(e) => setAnchorEl(e.currentTarget)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              width: '100%',
              padding: '8px 10px',
              borderRadius: 10,
              border: '1px solid transparent',
              background: 'transparent',
              cursor: 'pointer',
              transition: 'background 150ms ease',
              justifyContent: collapsed ? 'center' : 'flex-start',
              fontFamily: 'Inter, sans-serif',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
                color: 'white',
                fontWeight: 700,
                fontSize: '0.875rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxShadow: '0 2px 8px rgba(99,102,241,0.4)',
              }}
            >
              {userInitial}
            </div>
            {!collapsed && (
              <div style={{ flex: 1, minWidth: 0, textAlign: 'left', animation: 'fadeIn 0.2s ease-out' }}>
                <div
                  style={{
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    color: '#e2e8f0',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {user?.name || 'User'}
                </div>
                <div style={{ fontSize: '0.7rem', color: '#64748b', whiteSpace: 'nowrap' }}>
                  {roleLabel(user?.role)}
                </div>
              </div>
            )}
          </button>
        </div>
      </aside>

      <header
        style={{
          position: 'fixed',
          top: 0,
          left: sidebarWidth,
          right: 0,
          height: 64,
          background: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          padding: '0 1.5rem',
          gap: 12,
          zIndex: 1100,
          transition: 'left 280ms cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        <button
          onClick={() => setCollapsed((c) => !c)}
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#64748b',
            transition: 'all 150ms ease',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#f1f5f9';
            e.currentTarget.style.color = '#0f172a';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = '#64748b';
          }}
        >
          {collapsed ? <MenuIcon fontSize="small" /> : <MenuOpen fontSize="small" />}
        </button>

        <div style={{ flex: 1 }}>
          <span style={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a', letterSpacing: '-0.01em' }}>
            {pageTitle}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <NotificationBell />
          <div style={{ width: 1, height: 24, background: '#e2e8f0', margin: '0 4px', flexShrink: 0 }} />
          <button
            onClick={(e) => setAnchorEl(e.currentTarget)}
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
              border: 'none',
              cursor: 'pointer',
              color: 'white',
              fontWeight: 700,
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(99,102,241,0.3)',
              transition: 'all 150ms ease',
              fontFamily: 'Inter, sans-serif',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.06)';
              e.currentTarget.style.boxShadow = '0 4px 14px rgba(99,102,241,0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(99,102,241,0.3)';
            }}
          >
            {userInitial}
          </button>
        </div>
      </header>

      <Menu
        anchorEl={groupMenuAnchor}
        open={Boolean(groupMenuAnchor)}
        onClose={() => setGroupMenuAnchor(null)}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        PaperProps={{
          sx: {
            ml: 1,
            minWidth: 200,
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        {groupMenuItems.map((item) => (
          <MenuItem
            key={item.path}
            onClick={() => handleNavClick(item.path)}
            selected={isActive(item.path)}
            sx={{ gap: 1.5, borderRadius: '8px', mx: 0.5 }}
          >
            {item.icon}
            <Typography variant="body2" fontWeight={isActive(item.path) ? 600 : 500}>
              {item.text}
            </Typography>
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 220,
            borderRadius: '14px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 16px 40px rgba(0,0,0,0.1)',
            overflow: 'visible',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -6,
              right: 14,
              width: 12,
              height: 12,
              background: 'white',
              border: '1px solid #e2e8f0',
              borderBottom: 'none',
              borderRight: 'none',
              transform: 'rotate(45deg)',
              zIndex: 10,
            },
          },
        }}
      >
        <div style={{ padding: '12px 14px 8px', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
                color: 'white',
                fontWeight: 700,
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {userInitial}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#0f172a',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {user?.name}
              </div>
              <div
                style={{
                  fontSize: '0.75rem',
                  color: '#64748b',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {user?.email}
              </div>
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '2px 8px',
                borderRadius: 9999,
                background: '#f0fdf4',
                color: '#16a34a',
                fontSize: '0.7rem',
                fontWeight: 600,
                textTransform: 'capitalize',
              }}
            >
              {roleLabel(user?.role)}
            </span>
          </div>
        </div>

        <div style={{ padding: '6px' }}>
          <MenuItem onClick={() => setAnchorEl(null)} sx={{ borderRadius: '8px', gap: 1.5 }}>
            <Settings fontSize="small" sx={{ color: '#64748b' }} />
            <Typography variant="body2" fontWeight={500}>
              Settings
            </Typography>
          </MenuItem>
          <Divider sx={{ my: 0.5, borderColor: '#f1f5f9' }} />
          <MenuItem
            onClick={handleLogout}
            sx={{ borderRadius: '8px', gap: 1.5, color: '#ef4444', '&:hover': { background: '#fef2f2' } }}
          >
            <Logout fontSize="small" />
            <Typography variant="body2" fontWeight={500} color="inherit">
              Sign Out
            </Typography>
          </MenuItem>
        </div>
      </Menu>

      <main
        style={{
          marginLeft: sidebarWidth,
          marginTop: 64,
          flex: 1,
          minHeight: 'calc(100vh - 64px)',
          padding: '1.5rem',
          transition: 'margin-left 280ms cubic-bezier(0.4,0,0.2,1)',
          background: '#f8fafc',
        }}
      >
        <div style={{ animation: 'fadeInUp 0.35s ease-out both' }}>{children}</div>
      </main>
    </div>
  );
};

export default Layout;
